/**
 * Pas de quantité, minimum d’achat et prix dégressif — miroir du domaine backend.
 */

import type { PriceType } from './product';

const EPS = 1e-9;

export const QUANTITY_STEPS: Record<PriceType, readonly number[]> = {
  kg: [0.1, 0.25, 0.5, 1],
  litre: [0.1, 0.25, 0.5, 1],
  piece: [1, 2, 4, 6],
  botte: [1, 2, 3],
};

/** Au-delà, on prévient : le client qui teste n’achète plus. */
export const HIGH_MIN_QUANTITY: Record<PriceType, number> = {
  kg: 2,
  litre: 2,
  piece: 4,
  botte: 3,
};

export const MAX_QUANTITY_BREAKS = 3;

export type QuantityBreakKind = 'percent' | 'amount';

export interface QuantityBreak {
  readonly fromQuantity: number;
  readonly kind: QuantityBreakKind;
  readonly percent: number | null;
  readonly amountCents: number | null;
}

export function defaultQuantityStep(priceType: PriceType): number {
  return priceType === 'kg' || priceType === 'litre' ? 0.1 : 1;
}

export function isAllowedQuantityStep(priceType: PriceType, step: number): boolean {
  return QUANTITY_STEPS[priceType].some((candidate) => Math.abs(candidate - step) < EPS);
}

export function isMultipleOfStep(value: number, step: number): boolean {
  if (!Number.isFinite(value) || !Number.isFinite(step) || step <= 0) {
    return false;
  }
  const rounded = Math.round(value / step) * step;
  return Math.abs(rounded - value) < EPS;
}

export function isQuantityOnStep(quantity: number, step: number): boolean {
  return Number.isFinite(quantity) && quantity > 0 && isMultipleOfStep(quantity, step);
}

export function minQuantityWarning(
  priceType: PriceType,
  minQuantity: number,
  unit: string,
): string | undefined {
  if (minQuantity <= HIGH_MIN_QUANTITY[priceType]) {
    return undefined;
  }
  return `Un minimum de ${formatQuantity(minQuantity)} ${unit} peut freiner les petites commandes.`;
}

export type QuantityRulesVerdict =
  | {
      readonly ok: true;
      readonly quantityStep: number;
      readonly minQuantity: number;
      readonly quantityBreaks: readonly QuantityBreak[];
    }
  | { readonly ok: false; readonly message: string };

export function validateQuantityRules(
  priceType: PriceType,
  rawStep: number | null | undefined,
  rawMin: number | null | undefined,
  rawBreaks: readonly QuantityBreak[] | undefined,
  unitPriceCents: number,
): QuantityRulesVerdict {
  const quantityStep =
    rawStep === null || rawStep === undefined || Number.isNaN(rawStep)
      ? defaultQuantityStep(priceType)
      : rawStep;
  if (!isAllowedQuantityStep(priceType, quantityStep)) {
    return {
      ok: false,
      message: `Choisissez un pas proposé pour cette unité (${QUANTITY_STEPS[priceType].join(', ')}).`,
    };
  }

  const minQuantity =
    rawMin === null || rawMin === undefined || Number.isNaN(rawMin) ? quantityStep : rawMin;
  if (!Number.isFinite(minQuantity) || minQuantity <= 0) {
    return { ok: false, message: 'Le minimum d’achat doit être un nombre positif.' };
  }
  if (minQuantity < quantityStep - EPS || !isMultipleOfStep(minQuantity, quantityStep)) {
    return { ok: false, message: 'Le minimum d’achat doit être un multiple du pas de quantité.' };
  }

  const breaks = validateQuantityBreaks(rawBreaks ?? [], quantityStep, minQuantity, unitPriceCents);
  if (!breaks.ok) {
    return breaks;
  }
  return { ok: true, quantityStep, minQuantity, quantityBreaks: breaks.value };
}

function validateQuantityBreaks(
  raw: readonly QuantityBreak[],
  step: number,
  minQuantity: number,
  unitPriceCents: number,
):
  | { readonly ok: true; readonly value: readonly QuantityBreak[] }
  | { readonly ok: false; readonly message: string } {
  if (raw.length > MAX_QUANTITY_BREAKS) {
    return { ok: false, message: 'Trois paliers de prix au plus.' };
  }

  const seen = new Set<string>();
  const breaks: QuantityBreak[] = [];
  for (const entry of raw) {
    if (!Number.isFinite(entry.fromQuantity) || entry.fromQuantity <= minQuantity + EPS) {
      return {
        ok: false,
        message: 'Un palier de prix doit démarrer au-dessus du minimum d’achat.',
      };
    }
    if (!isMultipleOfStep(entry.fromQuantity, step)) {
      return { ok: false, message: 'La quantité d’un palier de prix doit suivre le pas choisi.' };
    }
    const key = String(Math.round(entry.fromQuantity / step));
    if (seen.has(key)) {
      return { ok: false, message: 'Deux paliers de prix ne peuvent pas partir de la même quantité.' };
    }
    seen.add(key);

    if (entry.kind === 'percent') {
      if (
        entry.percent === null ||
        !Number.isInteger(entry.percent) ||
        entry.percent < 1 ||
        entry.percent > 50
      ) {
        return {
          ok: false,
          message: 'La remise en pourcentage d’un palier doit être un entier entre 1 et 50.',
        };
      }
      breaks.push({
        fromQuantity: entry.fromQuantity,
        kind: 'percent',
        percent: entry.percent,
        amountCents: null,
      });
      continue;
    }

    if (entry.kind === 'amount') {
      if (
        entry.amountCents === null ||
        !Number.isInteger(entry.amountCents) ||
        entry.amountCents <= 0 ||
        entry.amountCents >= unitPriceCents
      ) {
        return {
          ok: false,
          message: 'La remise en euros d’un palier doit être inférieure au prix unitaire.',
        };
      }
      breaks.push({
        fromQuantity: entry.fromQuantity,
        kind: 'amount',
        percent: null,
        amountCents: entry.amountCents,
      });
      continue;
    }

    return { ok: false, message: 'Un palier de prix est soit un pourcentage, soit un montant.' };
  }

  return { ok: true, value: [...breaks].sort((a, b) => a.fromQuantity - b.fromQuantity) };
}

export function matchingQuantityBreak(
  quantity: number,
  breaks: readonly QuantityBreak[],
): QuantityBreak | undefined {
  let found: QuantityBreak | undefined;
  for (const entry of breaks) {
    if (quantity + EPS >= entry.fromQuantity) {
      found = entry;
    }
  }
  return found;
}

export function unitPriceWithBreaks(
  baseUnitCents: number,
  quantity: number,
  breaks: readonly QuantityBreak[],
): number {
  const matched = matchingQuantityBreak(quantity, breaks);
  if (matched === undefined) {
    return baseUnitCents;
  }
  if (matched.kind === 'percent' && matched.percent !== null) {
    return Math.max(0, Math.round((baseUnitCents * (100 - matched.percent)) / 100));
  }
  if (matched.kind === 'amount' && matched.amountCents !== null) {
    return Math.max(0, baseUnitCents - matched.amountCents);
  }
  return baseUnitCents;
}

export function formatQuantity(value: number): string {
  if (Number.isInteger(value)) {
    return String(value);
  }
  return String(value)
    .replace('.', ',')
    .replace(/0+$/u, '')
    .replace(/,$/u, '');
}
